import { useEffect, useRef, useState } from 'react';
import Button from '../Button.js';
import Icon from '../Icon.js';
import StepFrame from './StepFrame.js';
import Requests from '../Requests.js';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * Sends on arrival. The form before it was the last decision, so there is no
 * confirm step in front of this one.
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SubmitStep = ({
  onSent,
  payload,
  stepKey
}) => {
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  // Not the `cancelled` flag LookupStep uses. That ignores a second result;
  // this has to stop a second POST, or a dev double-render files two
  // escalations.
  const fired = useRef(false);
  const mobile = Utils.isMobile();
  const send = async () => {
    setError(null);
    try {
      let result = await Requests.submit(payload);
      setSent(result);
      onSent?.(result);
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => {
    if (fired.current) {
      return;
    }
    fired.current = true;
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const card = children => /*#__PURE__*/_jsx("div", {
    style: {
      ...Theme.styles.card(),
      boxShadow: Theme.boxShadow.card,
      padding: mobile ? '24px 20px' : '32px 32px'
    },
    children: children
  });
  if (sent) {
    return /*#__PURE__*/_jsx(StepFrame, {
      eyebrow: 'Sent',
      stepKey: 'sent',
      title: 'We have got it',
      children: card(/*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Icon, {
          color: Theme.colors.success,
          name: 'check-circle-fill',
          size: 30
        }), /*#__PURE__*/_jsx("span", {
          style: {
            ...Theme.textStyles.subHeading({
              fontSize: mobile ? 17 : 19
            }),
            display: 'block',
            margin: '14px 0 8px'
          },
          children: 'Your request has been sent'
        }), /*#__PURE__*/_jsx("span", {
          style: {
            ...Theme.textStyles.body({
              fontSize: 15,
              lineHeight: 1.65
            }),
            display: 'block'
          },
          children: `We will reach you at ${payload.customer.email_address}. Please allow 24-48 hours for us to review your request.`
        }), sent.reference && /*#__PURE__*/_jsx("span", {
          style: {
            ...Theme.textStyles.caption(),
            display: 'block',
            marginTop: 16
          },
          children: `Reference ${sent.reference}`
        })]
      }))
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(StepFrame, {
      actions: /*#__PURE__*/_jsx(Button, {
        icon: 'send-fill',
        label: 'Try again',
        onClick: send
      }),
      eyebrow: 'Opps!',
      stepKey: 'submit-error',
      title: 'There was an issue submitting your request',
      children: card(/*#__PURE__*/_jsxs("div", {
        style: {
          alignItems: 'flex-start',
          display: 'flex',
          gap: 12
        },
        children: [/*#__PURE__*/_jsx(Icon, {
          color: Theme.colors.error,
          name: 'house-exclamation-fill',
          size: 16,
          style: {
            flexShrink: 0,
            marginTop: 2
          }
        }), /*#__PURE__*/_jsx("span", {
          style: Theme.textStyles.body({
            color: Theme.colors.text(),
            fontSize: 14
          }),
          children: error
        })]
      }))
    });
  }
  return /*#__PURE__*/_jsx(StepFrame, {
    help: 'One moment while we send this.',
    stepKey: stepKey,
    title: 'Sending your request',
    children: /*#__PURE__*/_jsx("div", {
      "aria-busy": true,
      role: 'status',
      children: [64, 40, 52].map((width, index) => /*#__PURE__*/_jsx("div", {
        className: 'support-skeleton',
        style: {
          height: 14,
          marginBottom: 12,
          width: `${width}%`
        }
      }, index))
    })
  });
};
export default SubmitStep;